import { WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR } from "@/constants";
import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import {
    globalProductIdManagerToken,
    globalProductVariantsIdManagerToken,
} from "@/tokens";
import { RewardsEarnPointsProductPageWidgetDefinition } from "@apps/rewards/definitions";
import RewardsDiResolverService from "@apps/rewards/services/RewardsDiResolverService/RewardsDIResolverService";
import type { RewardsWidgetInjectorConfig } from "@apps/rewards/types";
import { ProductIdFromStaticProvider } from "@lib/ProductIdManager/ProductIdFromStaticProvider";
import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
import { ProductVariantIdFromAttributeProvider } from "@lib/ProductVariantIdManager/ProductVariantIdFromAttributeProvider";
import { ProductVariantIdFromUrlProvider } from "@lib/ProductVariantIdManager/ProductVariantIdFromUrlProvider";
import { ProductVariantsIdManager } from "@lib/ProductVariantIdManager/ProductVariantIdManager";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import type AttributesObserver from "@ui-utils/AttributesObserver/AttributesObserver";
import {
    parseIntDataAttr,
    parseIntFromGidDataAttr,
    parseOptionalBooleanDataAttr,
} from "@ui-utils/Injector/utils";
import { map, switchMap, tap } from "rxjs";

const PRODUCT_ID_ATTR = "gw-product-id";
const VARIANT_ID_ATTR = "gw-variant-id";

interface RewardsEarnPointsProductPageWidgetInjectorConfig
    extends RewardsWidgetInjectorConfig {
    attributesObserver: AttributesObserver;
}

class RewardsEarnPointsProductPageWidgetInjector {
    constructor(
        private readonly config: RewardsEarnPointsProductPageWidgetInjectorConfig
    ) {}

    start(): void {
        this.config.logger.debug(
            "RewardsEarnPointsProductPageWidgetInjector start",
            {
                placeholderSelector: this.config.placeholderSelector,
            }
        );

        const widgetName =
            REWARDS_WIDGET_NAMES.REWARDS_EARN_POINTS_PRODUCT_PAGE_WIDGET;
        const diResolverService = new RewardsDiResolverService();
        const { globalProductIdManager, globalProductVariantsIdManager } =
            diResolverService.resolveAll({
                globalProductIdManager: globalProductIdManagerToken,
                globalProductVariantsIdManager:
                    globalProductVariantsIdManagerToken,
            });

        const targets$ = this.config.mutationObserverInjector.register({
            targetSelector: this.config.placeholderSelector,
        });

        targets$
            .pipe(
                switchMap((placeholder) => {
                    this.config.logger?.debug(
                        "RewardsEarnPointsProductPageWidgetInjector found placeholder",
                        {
                            placeholder,
                        }
                    );

                    const productIdFromDataAttr =
                        parseIntDataAttr(placeholder, PRODUCT_ID_ATTR) ||
                        parseIntFromGidDataAttr(placeholder, PRODUCT_ID_ATTR);

                    const productIdManager =
                        productIdFromDataAttr === null
                            ? globalProductIdManager
                            : new ProductIdManager();

                    if (productIdFromDataAttr !== null) {
                        productIdManager.registerProvider(
                            new ProductIdFromStaticProvider(
                                productIdFromDataAttr
                            )
                        );
                    }

                    const productVariantIdFromDataAttr =
                        parseIntDataAttr(placeholder, VARIANT_ID_ATTR) ||
                        parseIntFromGidDataAttr(placeholder, VARIANT_ID_ATTR);

                    const productVariantsIdManager =
                        productVariantIdFromDataAttr === null
                            ? globalProductVariantsIdManager
                            : new ProductVariantsIdManager();

                    if (productVariantIdFromDataAttr !== null) {
                        productVariantsIdManager.registerProvider(
                            new ProductVariantIdFromAttributeProvider(
                                this.config.attributesObserver,
                                placeholder,
                                VARIANT_ID_ATTR
                            )
                        );
                        productVariantsIdManager.registerProvider(
                            new ProductVariantIdFromUrlProvider()
                        );
                    }
                    return productIdManager.productId$.pipe(
                        map((productId) => ({
                            placeholder,
                            productId,
                            productVariantsIdManager,
                        }))
                    );
                }),
                tap(({ placeholder, productId, productVariantsIdManager }) => {
                    if (!productId) {
                        throw new Error(
                            `Placeholder does not have data-${PRODUCT_ID_ATTR} attribute.`
                        );
                    }

                    void (async () => {
                        const RewardsEarnPointsProductPageWidget =
                            await this.config.dynamicImportManager.importDefault(
                                RewardsEarnPointsProductPageWidgetDefinition,
                                true
                            );

                        const widget = new RewardsEarnPointsProductPageWidget(
                            {
                                target: placeholder,
                                productId,
                                isWidgetShadowRootEnabled:
                                    parseOptionalBooleanDataAttr(
                                        placeholder,
                                        WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                                    ),
                            },
                            this.config.logger,
                            productVariantsIdManager
                        );

                        await widget.init();

                        this.config.eventsCollector.pushEvent(
                            this.config.eventsFactory.createWidgetInjected(
                                widgetName
                            )
                        );

                        dispatchSdkEvent(
                            new WidgetInjectedSdkEvent(widgetName)
                        );
                    })();
                })
            )
            .subscribe();
    }
}

export default RewardsEarnPointsProductPageWidgetInjector;
